import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { Tournament } from "../types";
import { analyzePlayerWaitingTimes, detectDuplicatePlayers, type PlayerMatchSchedule } from "../utils/scheduleUtils";

interface WaitingTimeReportDialogProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament | null;
}

const WaitingTimeReportDialog: React.FC<WaitingTimeReportDialogProps> = ({
  open,
  onClose,
  tournament,
}) => {
  if (!tournament) return null;

  const analysis = analyzePlayerWaitingTimes(tournament);
  const avg = analysis.tournamentAverage;
  const duplicatePlayers = detectDuplicatePlayers(tournament);

  // Colour based on how far a player's average break deviates from the tournament average
  const getDeviationColor = (playerAvg: number): string => {
    const deviation = Math.abs(playerAvg - avg);
    if (deviation <= 5) return "#4caf50";   // within 5 min — very fair
    if (deviation <= 15) return "#ff9800";  // within 15 min — slight imbalance
    return "#f44336";                        // >15 min — notable imbalance
  };

  const getDeviationIcon = (playerAvg: number) => {
    const deviation = Math.abs(playerAvg - avg);
    if (deviation <= 5) return <CheckIcon fontSize="small" sx={{ color: "#4caf50" }} />;
    return <WarningIcon fontSize="small" sx={{ color: deviation <= 15 ? "#ff9800" : "#f44336" }} />;
  };

  const getBreakTooltip = (player: PlayerMatchSchedule, breakIndex: number): string => {
    const matchBefore = player.matches[breakIndex];
    const matchAfter  = player.matches[breakIndex + 1];
    if (!matchBefore || !matchAfter) return "";
    const fmt = (iso: string) =>
      new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const stage = (m: typeof matchBefore) => m.isGroupStage ? "Group" : "Knockout";
    return [
      `Break between:`,
      `▶ ${stage(matchBefore)} vs ${matchBefore.opponent}  ends ${fmt(matchBefore.endTime)} (Court ${matchBefore.courtNumber})`,
      `▶ ${stage(matchAfter)} vs ${matchAfter.opponent}  starts ${fmt(matchAfter.startTime)} (Court ${matchAfter.courtNumber})`,
    ].join("\n");
  };

  const getMinBreakIndex = (p: PlayerMatchSchedule) =>
    p.waitingTimes.indexOf(Math.min(...p.waitingTimes));
  const getMaxBreakIndex = (p: PlayerMatchSchedule) =>
    p.waitingTimes.indexOf(Math.max(...p.waitingTimes));

  // Sort by absolute deviation from average (most imbalanced first)
  const sortedPlayers = [...analysis.playerSchedules].sort(
    (a, b) => Math.abs(b.averageWaiting - avg) - Math.abs(a.averageWaiting - avg)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingUpIcon />
          <Typography variant="h6" component="span">
            Waiting Time Fairness Report
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Duplicate player error — shown first so it's impossible to miss */}
        {duplicatePlayers.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              ⛔ Data integrity error: players registered on multiple teams
            </Typography>
            {duplicatePlayers.map((d, i) => (
              <Typography key={i} variant="body2">
                • <strong>{d.playerName}</strong>: {d.teamNames.join(' · ')}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Remove the duplicate registrations — the schedule and fairness score are unreliable until this is fixed.
            </Typography>
          </Alert>
        )}

        {/* Summary */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "background.default" }}>
          <Typography variant="h6" gutterBottom>
            Tournament Summary
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Average Break
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {Math.round(avg)} min
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fairness Score
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color:
                    analysis.fairnessScore > 70
                      ? "#4caf50"
                      : analysis.fairnessScore > 40
                      ? "#ff9800"
                      : "#f44336",
                }}
              >
                {Math.round(analysis.fairnessScore)}/100
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Players
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {analysis.playerSchedules.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Warnings */}
        {analysis.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Imbalance Warnings
            </Typography>
            {analysis.warnings.map((w, i) => (
              <Typography key={i} variant="body2">• {w}</Typography>
            ))}
          </Alert>
        )}

        {/* Player table */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Player Breakdown
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Sorted by deviation from average (most imbalanced first)
        </Typography>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Player</strong></TableCell>
                <TableCell align="center"><strong>Matches</strong></TableCell>
                <TableCell align="center"><strong>Min Break</strong></TableCell>
                <TableCell align="center"><strong>Avg Break</strong></TableCell>
                <TableCell align="center"><strong>Max Break</strong></TableCell>
                <TableCell align="center"><strong>vs Average</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPlayers.map((player) => {
                const deviation = player.averageWaiting - avg;
                return (
                  <TableRow key={player.playerId}>
                    <TableCell>{player.playerName}</TableCell>
                    <TableCell align="center">{player.matches.length}</TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={<Box sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}>{getBreakTooltip(player, getMinBreakIndex(player))}</Box>}
                        placement="top"
                        arrow
                      >
                        <span style={{ cursor: "help" }}>{player.minWaiting}m</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${Math.round(player.averageWaiting)}m`}
                        size="small"
                        sx={{
                          bgcolor: getDeviationColor(player.averageWaiting),
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={<Box sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}>{getBreakTooltip(player, getMaxBreakIndex(player))}</Box>}
                        placement="top"
                        arrow
                      >
                        <span style={{ cursor: "help" }}>{player.maxWaiting}m</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{ color: deviation > 0 ? "#e65100" : deviation < 0 ? "#1565c0" : "text.secondary" }}
                      >
                        {deviation > 0 ? `+${Math.round(deviation)}m` : deviation < 0 ? `${Math.round(deviation)}m` : "="}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{getDeviationIcon(player.averageWaiting)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legend */}
        <Paper sx={{ p: 2, mt: 3, bgcolor: "background.default" }}>
          <Typography variant="subtitle2" gutterBottom>
            Legend
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#4caf50", borderRadius: 1 }} />
              <Typography variant="caption">Within 5 min of average</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#ff9800", borderRadius: 1 }} />
              <Typography variant="caption">5–15 min from average</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: "#f44336", borderRadius: 1 }} />
              <Typography variant="caption">&gt;15 min from average</Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Typography variant="caption" color="text.secondary">
              <strong>Fairness Score:</strong> 100 = all players have identical average breaks; lower = more imbalanced
            </Typography>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaitingTimeReportDialog;
